import React from "react";
import { Container, Card, CardBody, CardHeader, Row, Col, Badge, Table, Button } from "reactstrap";

// Blue theme tokens (match the app).
const BLUE = "#123a7a";
const ACCENT = "#1e4fbf";

const Step = ({ n, team, title, children }) => (
  <Card className="shadow-sm border-0 mb-3">
    <CardBody className="d-flex" style={{ gap: 16 }}>
      <div style={{
        flex: "0 0 44px", width: 44, height: 44, borderRadius: "50%",
        background: ACCENT, color: "#fff", display: "flex", alignItems: "center",
        justifyContent: "center", fontWeight: 700, fontSize: 18,
      }}>{n}</div>
      <div>
        <div className="text-uppercase small font-weight-bold" style={{ letterSpacing: 1, color: ACCENT }}>{team}</div>
        <h4 className="mb-1" style={{ color: BLUE }}>{title}</h4>
        <div className="text-muted">{children}</div>
      </div>
    </CardBody>
  </Card>
);

// ---- small building blocks for the illustrated screen mockups ----
const Btn = ({ children, light }) => (
  <span style={{
    display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
    background: light ? "#fff" : ACCENT, color: light ? BLUE : "#fff",
    border: light ? "1px solid #cbd6e6" : "none", marginLeft: 4, whiteSpace: "nowrap",
  }}>{children}</span>
);
const Pill = ({ children, bg }) => (
  <span style={{
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: "#fff", background: bg, whiteSpace: "nowrap",
  }}>{children}</span>
);
const Figure = ({ title, caption, children }) => (
  <div className="border rounded overflow-hidden h-100" style={{ background: "#f6f9fc" }}>
    <div className="p-3" style={{ background: "#fff" }}>{children}</div>
    <div className="p-3 border-top">
      <div className="font-weight-bold" style={{ color: BLUE }}>{title}</div>
      <div className="small text-muted">{caption}</div>
    </div>
  </div>
);
const MockTable = ({ head, children }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
      <thead>
        <tr>{head.map((h) => (
          <th key={h} style={{ textAlign: "left", padding: "4px 6px", color: "#8898aa", textTransform: "uppercase", fontSize: 10, borderBottom: "1px solid #e9ecef" }}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {children.map((row, i) => (
          <tr key={i}>{row.map((c, j) => (
            <td key={j} style={{ padding: "6px", borderBottom: "1px solid #f1f4f8", whiteSpace: "nowrap" }}>{c}</td>
          ))}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BUCKETS = [
  ["Matched", "success", "UTR found on both sides and the amount matches. Fully reconciled — no action."],
  ["Amount Mismatch", "warning", "UTR found on both sides but the amounts differ. Investigate the difference (fee, partial, rounding)."],
  ["Missing In System", "danger", "The line is in the bank/provider statement but NOT in our system. The bank moved money we have no record of — book it or query the LSP."],
  ["Missing In Bank", "info", "The record is in our system but NOT in the statement. We recorded it but the bank feed doesn't confirm it — verify the payout/collection actually happened."],
  ["Reversed", "dark", "The statement marks the line as reversed / refunded / returned. Recorded separately."],
  ["Info", "secondary", "Informational rows with no amount / no match — carried for completeness."],
];

const Training = () => {
  return (
    <Container className="mt--7" fluid>
      {/* Banner */}
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
          <div>
            <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC · SOP &amp; Training</h6>
            <h2 className="text-white mb-0">Reconciliation — Standard Operating Procedure</h2>
            <p className="text-light mb-0 mt-2" style={{ maxWidth: 720 }}>
              How the reconciliation tool works, and the exact step-by-step each team follows —
              Accounts, Operations, the LSP partner, and IT.
            </p>
          </div>
          <Button color="light" size="sm" onClick={() => window.print()}>
            <i className="fa fa-download mr-1" /> Save as PDF
          </Button>
        </CardBody>
      </Card>

      {/* What it does */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0"><h3 className="mb-0" style={{ color: BLUE }}>1 · What the tool does</h3></CardHeader>
        <CardBody className="pt-0 text-muted">
          <p>
            The <strong>Reconciliation</strong> tool (sidebar → <em>Reconciliation → Credit Line / Loan</em>) compares
            <strong> our system records</strong> against the <strong>bank / payment-provider statement</strong> you upload,
            and sorts every transaction into a clear bucket — matched, mismatched, or an exception that needs action.
          </p>
          <ul>
            <li><strong>Credit Line</strong> — reconciliation for the credit-line / withdrawal product.</li>
            <li><strong>Loan</strong> — the Bank Reconciliation tool: upload a <em>Payin</em> (collection) or <em>Payout</em> (disbursement) statement and review matched vs. exception rows.</li>
          </ul>
          <p className="mb-0">
            Each upload becomes a <strong>run</strong> that is saved with its counts, so the history is always auditable.
          </p>
        </CardBody>
      </Card>

      {/* How matching works */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0"><h3 className="mb-0" style={{ color: BLUE }}>2 · How the matching works</h3></CardHeader>
        <CardBody className="pt-0 text-muted">
          <p>Every statement line is matched to our ledger on the <strong>UTR</strong> (the bank reference), then classified:</p>
          <Row>
            <Col md="6" className="mb-3">
              <div className="p-3 rounded" style={{ background: "rgba(30,79,191,0.06)" }}>
                <div className="font-weight-bold" style={{ color: BLUE }}>Payin (collection) statement</div>
                <div className="small">Matched against the <code>payment</code> collection table. For a matched payment the tool also shows the settlement split — <strong>Principal / Interest / Fee</strong> — and the <strong>Loan ID</strong>.</div>
              </div>
            </Col>
            <Col md="6" className="mb-3">
              <div className="p-3 rounded" style={{ background: "rgba(30,79,191,0.06)" }}>
                <div className="font-weight-bold" style={{ color: BLUE }}>Payout (disbursement) statement</div>
                <div className="small">Matched against our disbursals and outgoing ledger (loan disbursals + payment transactions). Shows the <strong>Loan ID</strong> for each matched payout.</div>
              </div>
            </Col>
          </Row>
          <h4 className="mt-2" style={{ color: BLUE }}>Reconciliation buckets</h4>
          <div style={{ overflowX: "auto" }}>
            <Table className="align-items-center" size="sm">
              <thead className="thead-light">
                <tr><th>Status</th><th>Meaning &amp; action</th></tr>
              </thead>
              <tbody>
                {BUCKETS.map(([label, color, desc]) => (
                  <tr key={label}>
                    <td style={{ whiteSpace: "nowrap" }}><Badge color={color} pill>{label}</Badge></td>
                    <td className="text-muted">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* The SOP steps */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <h3 className="mb-0" style={{ color: BLUE }}>3 · The process — who does what</h3>
          <small className="text-muted">Follow these in order for every reconciliation cycle.</small>
        </CardHeader>
        <CardBody className="pt-0">
          <Step n="1" team="Accounts Team" title="Download &amp; upload the statement">
            Download the settlement / VA statement from the provider (Juspay, IDFC, Razorpay) for the period.
            Open <strong>Reconciliation → Loan</strong> and click <strong>Upload Payin</strong> (for collections) or
            <strong> Upload Payout</strong> (for disbursements). The file can be CSV or XLSX — the tool auto-detects the
            header row and the UTR / amount / date / status columns.
          </Step>
          <Step n="2" team="System" title="Automatic reconciliation">
            The tool parses the file, matches each line to our ledger by UTR, and creates a <strong>run</strong> showing
            Rows · Matched · Amount Mismatch · Missing (System) · Missing (Bank) · Reversed.
          </Step>
          <Step n="3" team="Operations Team" title="Capture the mismatches">
            Open the run and review the exception rows — <em>Missing In System</em>, <em>Missing In Bank</em> and
            <em> Amount Mismatch</em>. For each, use <strong>Resolve</strong> to set a status (Open / Investigating /
            Resolved / Ignored) with a note, or <strong>Action</strong> to record a corrective action (e.g. “Mark payout
            SUCCESS”, “Book in system”).
          </Step>
          <Step n="4" team="Operations + LSP Partner" title="Prepare &amp; share the mismatch sheet">
            Compile the unresolved exceptions into a mismatch sheet and share it with the <strong>LSP partner</strong> so
            both sides agree on what is genuinely missing or different (missing loans, missing repayments, amount gaps).
          </Step>
          <Step n="5" team="IT Team" title="Upload the corrected / missing data">
            IT uploads the data agreed with the LSP (the sheet shared from the front end) so our system matches the bank
            statement. After this cycle, re-run the reconciliation — the previously-missing rows should now show as
            <strong> Matched</strong>.
          </Step>
        </CardBody>
      </Card>

      {/* Screen guide — illustrated mockups of the real screens */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <h3 className="mb-0" style={{ color: BLUE }}>4 · Screen guide</h3>
          <small className="text-muted">What each reconciliation screen looks like.</small>
        </CardHeader>
        <CardBody className="pt-0">
          <Row>
            {/* Runs list */}
            <Col lg="6" className="mb-4">
              <Figure title="Runs list — upload &amp; history" caption="Upload Payin / Payout, then browse past runs with their counts.">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong style={{ color: BLUE }}>Bank Reconciliation</strong>
                  <div>
                    <Btn light>↓ Upload Payin</Btn> <Btn>↑ Upload Payout</Btn>
                  </div>
                </div>
                <MockTable head={["When", "Type", "File", "Rows", "Matched", "Miss(Sys)"]}>
                  {[
                    ["07-26 16:52", <Pill bg="#11998e">Payin</Pill>, "statement_…xlsx", "106", "0", "106"],
                    ["07-26 15:00", <Pill bg="#fb6340">Payout</Pill>, "statement_…xlsx", "7", "0", "7"],
                  ]}
                </MockTable>
              </Figure>
            </Col>

            {/* Run detail metrics */}
            <Col lg="6" className="mb-4">
              <Figure title="Run detail — metrics" caption="Every run summarises into these tiles before you open the entries.">
                <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                  {[["Rows", "106", "#32325d"], ["Matched", "84", "#2dce89"], ["Mismatch", "3", "#fb6340"],
                    ["Missing Sys", "12", "#f5365c"], ["Missing Bank", "5", "#5e72e4"], ["Reversed", "2", "#32325d"]].map(([l, v, c]) => (
                    <div key={l} style={{ flex: "1 0 30%", minWidth: 90 }}>
                      <div className="text-uppercase text-muted" style={{ fontSize: 10, fontWeight: 700 }}>{l}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</div>
                    </div>
                  ))}
                </div>
              </Figure>
            </Col>

            {/* Entries table */}
            <Col lg="6" className="mb-4">
              <Figure title="Entries — match buckets" caption="Each line's Match status, File vs System amount and Loan ID.">
                <MockTable head={["Reference", "Loan", "Match", "File ₹", "System ₹"]}>
                  {[
                    ["620823188531", "642", <Pill bg="#2dce89">Matched</Pill>, "13,230", "13,230"],
                    ["536025053033", "626", <Pill bg="#fb6340">Amt Mismatch</Pill>, "9,469", "9,500"],
                    ["000481377412", "—", <Pill bg="#f5365c">Missing In System</Pill>, "4,410", "—"],
                    ["TXN-1781…C96", "584", <Pill bg="#5e72e4">Missing In Bank</Pill>, "—", "5,375"],
                  ]}
                </MockTable>
              </Figure>
            </Col>

            {/* Resolve / action */}
            <Col lg="6" className="mb-4">
              <Figure title="Resolve / Action" caption="Set a resolution status or record a corrective action on an exception.">
                <div className="border rounded p-3" style={{ background: "#fff" }}>
                  <div className="font-weight-bold mb-2" style={{ color: BLUE }}>Update resolution</div>
                  <div className="text-uppercase text-muted" style={{ fontSize: 10, fontWeight: 700 }}>Resolution status</div>
                  <div className="border rounded px-2 py-1 mb-2 d-flex justify-content-between" style={{ background: "#f6f9fc" }}>
                    <span>Investigating</span><span className="text-muted">▾</span>
                  </div>
                  <div className="text-uppercase text-muted" style={{ fontSize: 10, fontWeight: 700 }}>Note</div>
                  <div className="border rounded px-2 py-3 mb-3" style={{ background: "#f6f9fc", fontSize: 12, color: "#8898aa" }}>Checking with bank ops…</div>
                  <div className="text-right"><Btn light>Cancel</Btn> <Btn>Save</Btn></div>
                </div>
              </Figure>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Roadmap */}
      <Card className="shadow mb-5">
        <CardHeader className="border-0"><h3 className="mb-0" style={{ color: BLUE }}>5 · Coming next</h3></CardHeader>
        <CardBody className="pt-0 text-muted">
          <div className="d-flex align-items-start" style={{ gap: 12 }}>
            <Badge color="primary" pill className="mt-1">Planned</Badge>
            <div>
              <strong>IT self-serve upload of missing loans &amp; repayments.</strong> A front-end feature so IT can
              directly upload the missing loan and repayment records (as shared by the LSP) — removing the manual step and
              closing the reconciliation loop faster.
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
};

export default Training;
